import {Layout} from '../components/Layout/Layout';
import styles from './hut.module.scss';
import {Suspense, useRef} from 'react';
import Image from 'next/image';
import {IconicList} from "../components/IconicList/IconicList";
import {Screen} from "../components/Screen/Screen";
import {TextBlock} from "../components/TextBlock/TextBlock";
import {ActionButton} from "../components/ActionButton/ActionButton";
import {clss} from "../src/utils";
import {SocialIcons} from "../components/SocialIcons/SocialIcons";
import bckHut1 from "../public/images/bck-hut1.png";
import bckHut2 from "../public/images/bck-hut2.png";
import bckHouse from "../public/images/bck-house.png";
import bckSpace from "../public/images/bck-space.png";
import bckFact2 from "../public/images/fact2.png";
import bckSomethingNew from "../public/images/bck-somethingnew.png";

import activitySurfing from '../public/images/activity-surfing.png';
import activitySunbath from '../public/images/activity-sunbath.png';
import activityHiking from '../public/images/activity-hiking.png';
import activityFood from '../public/images/activity-food.png';
import activityWorkshop from '../public/images/activity-workshop.png';
import activityMeet from '../public/images/activity-meet.png';

import dynamic from "next/dynamic";
import * as gtag from "../src/gtag";
import {useSmoothScroll} from "../components/SmoothScroll/SmoothScroll";
import {ImageWithBlur} from '../components/ImageWIthBlur/ImageWithBlur';
import {CTAScreen} from "../components/CTAScreen/CTAScreen";
import {FeedbackFormScreen} from "../components/FeedbackFormScreen/FeedbackFormScreen";

const DynamicCarousel = dynamic(() => import('../components/Carousel/Carousel'), {
  suspense: true,
})

export default function Media() {
  const getInTouchForm = useRef<HTMLDivElement>(null);
  const pricesScreen = useRef<HTMLDivElement>(null);

  const withSmoothScroll =  useSmoothScroll();

  return (
    <Layout title={'Barbaros Hut - A Home For Digital Nomads In Peniche Portugal'} subUrl={'hut'} contactsRef={getInTouchForm}
            description={'Barbaros Hut is a nomad friendly coliving space in Peniche, Portugal. Enjoy combining work and activities like hiking, yoga, cooking, and surfing classes'}
            ldJson={{
              "@context": "https://schema.org",
              "@type": "WebSite",
              "name": "Barbaros Hut",
              "alternateName": "A Home For Digital Nomads",
              "url": "https://barbaros.satelstories.com/hut/",
              "description": "Barbaros Hut is a nomad friendly coliving space in Peniche, Portugal. Enjoy combining work and activities like hiking, yoga, cooking, and surfing classes",
              "inLanguage": "en-US",
            }}
    >
      <Screen id={styles.first}>
        <div className={styles.container1}>
        <div className={styles.descriptionContainer}>
          <TextBlock main={true} titleRegular={'Barbaros '} titleBold={'Hut'}
                     slogan={'A home for remote workers'}
                     description={'Do you work remotely and want to travel? Then Barbaros Hut is a place for you. An oceanside house in Peniche, Portugal is designed for digital nomads to live, work, and enjoy.'}/>
          <div className={styles.buttonRow}>
            <ActionButton label={"Learn more"} onClick={() => {
              withSmoothScroll(() => pricesScreen.current!.scrollIntoView());
              gtag.event({
                action: "internal_navigate",
                label: "contacts",
                label2: "call_to_action_learnmore"
              })
            }}/>
          </div>
        </div>
          <div className={styles.extraBackground}>
            <ImageWithBlur src={bckHut2} draggable={false} loading={"lazy"} alt={"Remote coworking"}
                   layout={"responsive"} objectFit={"cover"} sizes={"(max-width: 565px) 100vw, 565px"}/>
          </div>
        </div>
        <div className={styles.container2}>
          <ImageWithBlur src={bckHut1} draggable={false}  loading={"lazy"} alt={"Remote work while travelling"}
                 layout={"responsive"} objectFit={"cover"} sizes={"(max-width: 768px) 100vw, 706px"}/>
        </div>
      </Screen>

      <CTAScreen titleRegular={'About '} titleBold={'the Hut'}
                 slogan={'By nomads for nomads'}
                 imageSrc={bckHouse} imageAlt={'Digital nomads coworking house in Portugal'} imageHiddenOnMobile={true}
                 description={<p>Barbaros Hut is a small house on the ocean coast in Peniche, Portugal. It is a nomad
                   accommodation meaning it’s created for remote workers rather than tourists.
                 </p>}
                 gray={true}
      >
        <h3 className={styles.iconicListHeader}>What’s included? </h3>

        <IconicList items={[
          {icon: 'palm', description: 'Accommodation right next to the ocean'},
          {icon: 'chair', description: 'Equipped comfortable workplace'},
          {icon: 'wifi', description: 'High-speed reliable Internet connection'},
          {icon: 'coffee', description: 'A common kitchen with everything one needs'},
          {icon: 'man', description: 'Welcome gift and a guarantee of a great time'},
          {icon: 'mask', description: 'Community of other nomads and travelers'},
        ]} rowWidth={2}/>


        <Suspense fallback={<></>}>
          <DynamicCarousel />
        </Suspense>
      </CTAScreen>

      <CTAScreen titleRegular={'Packages and '} titleBold={'Prices'}
                 imageSrc={bckSpace} imageAlt={'Inside the coliving house in Portugal'}
                 inlineForm={{
                   buttonLabel: 'Get a trip',
                   eventLabel: 'hut'
                 }}
                 description={<p>The packages we offer are custom-made: we craft each based on your goals and
                   budget.
                 </p>}
                 right={true}
                 ref={pricesScreen}
      >
        <div className={styles.packagesTable}>
          <div className={styles.col}>
            <div>
              <div className={styles.th}>Work</div>
              <div className={styles.td}>
                <div className={styles.textDescription}>
                  Just the basics: suitable for those who come to work and want to decide on extra activities on the spot.
                </div>
                <ul className={styles.features}>
                  <li>accommodation at the oceanside</li>
                  <li>workplace and coworking space</li>
                  <li>equipped kitchen</li>
                  <li>unlimited coffee and snacks</li>
                  <li>2 surfing lessons</li>
                  <li>community of other nomads</li>
                  <li>extra activities*</li>
                </ul>
              </div>
            </div>
            <div className={styles.priceBox}>
              <div className={styles.price}>From €850 / week</div>
              <div className={styles.disclaimer}>for 2 people</div>
            </div>
          </div>

          <div className={clss(styles.col, styles.odd)}>
            <div>
              <div className={styles.th}>Workation</div>
              <div className={styles.td}>
                <div className={styles.textDescription}>
                  Like a summer camp for remote workers: all-inclusive and always something fun to do when you’re not working. See the upcoming dates in <a href={"/events"}>Events</a>.
                </div>
                <ul className={styles.features}>
                  <li>accommodation at the oceanside</li>
                  <li>workplace and coworking space</li>
                  <li>breakfast and kitchen available</li>
                  <li>surf lessons with equipment</li>
                  <li>yoga sessions</li>
                  <li>hiking</li>
                  <li>trips and tours</li>
                  <li>barbeque nights and parties</li>
                </ul>
              </div>
            </div>
            <div className={styles.priceBox}>
              <div className={styles.price}>From €1290 / week</div>
              <div className={styles.disclaimer}>for 2 people</div>
            </div>
          </div>

          <div className={styles.col}>
            <div>
              <div className={styles.th}>Vacation</div>
              <div className={styles.td}>
                <div className={styles.textDescription}>
                  Some of us just want to chill, and there’s no harm in that. In that case, we’ll craft a program just for you! Contact us for details.
                </div>
              </div>
            </div>
            <div className={styles.priceBox}>
              <div className={styles.price}>From €1290 / week</div>
              <div className={styles.disclaimer}>for 2 people</div>
            </div>
          </div>

        </div>
      </CTAScreen>

      <CTAScreen titleRegular={'The '} titleBold={'Experience'}
                 slogan={'Treat it as a lifestyle'}
                 imageSrc={bckFact2} imageAlt={''}
                 description={<p>Ocean? Check. Equipped workplace and stable Internet? Check. People? Check. In Barbaros
                   Hut, you have everything a smart digital worker needs to work; you also have everything a person
                   needs to keep their mind off the office stuff and rest. Let’s roll!
                 </p>}
                 gray={true} right={true}
                 backgroundText={{
                   text: <><b>Barbaros fact:</b> an hour of work followed by a walk along the oceanside is
                     proved to be more efficient!</>,
                   className: styles.fact,
                 }}
      />

      <Screen id={styles.activities}>
        <div className={styles.activityCol}>
          <div className={styles.activity}>
            <div className={styles.activityContent}>
              <div className={styles.picture}><ImageWithBlur src={activitySurfing} width={126} height={126} alt={"Surf lessons and board rental"}/>
              </div>
              <div className={styles.title}>Try surfing lessons with certified coaches</div>
              <div className={styles.content}>Custom-made surfing packages with equipment included</div>
            </div>
          </div>
          <div className={styles.activity}>
            <div className={styles.activityContent}>
              <div className={styles.picture}><ImageWithBlur src={activitySunbath} width={126} height={126} alt={"Atlantic ocean beach in Peniche"}/>
              </div>
              <div className={styles.title}>Sunbath at the oceanic beach</div>
              <div className={styles.content}>Peniche beach is clean and positively huge — definitely enough space for
                everyone
              </div>
            </div>
          </div>
          <div className={clss(styles.activity, styles.col12)}>
            <div className={styles.activityContent}>
              <div className={styles.picture}><ImageWithBlur src={activityHiking} width={126} height={126} alt={"Yoga at the beach"}/>
              </div>
              <div className={styles.title}>Join hiking and yoga sessions</div>
              <div className={styles.content}>Enjoy the oceanside while connecting to the inner you</div>
            </div>
          </div>
        </div>
        <div className={clss(styles.activityCol, styles.upper)}>
          <div className={styles.activity}>
            <div className={styles.activityContent}>
              <div className={styles.picture}><ImageWithBlur src={activityFood} width={126} height={126} alt={"Portuguese national food"}/></div>
              <div className={styles.title}>Explore local food and drinks</div>
              <div className={styles.content}>
                Join the Portuguese dinner experience and enjoy a fresh pastel de nata at Hut’s breakfast
              </div>
            </div>
          </div>
          <div className={styles.activity}>
            <div className={styles.activityContent}>
              <div className={styles.picture}><ImageWithBlur src={activityWorkshop} width={126} height={126} alt={"Beautiful coastal place"}/>
              </div>
              <div className={styles.title}>Expand your horizons</div>
              <div className={styles.content}>Explore nearby towns, join our hobby classes, and try new exciting
                things!
              </div>
            </div>
          </div>
          <div className={clss(styles.activity, styles.col12)}>
            <div className={styles.activityContent}>
              <div className={styles.picture}><ImageWithBlur src={activityMeet} width={126} height={126} alt={"Digital nomads community"}/></div>
              <div className={styles.title}>Meet other nomads from all around the world</div>
              <div className={styles.content}>Co-work, join barbecue parties, visit movie nights and have fun</div>
            </div>
          </div>
        </div>
        <div className={clss(styles.activityCol, styles.col3)}>
          <div className={styles.activity}>
            <div className={styles.activityContent}>
              <div className={styles.picture}><ImageWithBlur src={activityHiking} width={126} height={126}/>
              </div>
              <div className={styles.title}>Join hiking and yoga sessions</div>
              <div className={styles.content}>Enjoy the oceanside while connecting to the inner you</div>
            </div>
          </div>
          <div className={styles.activity}>
            <div className={styles.activityContent}>
              <div className={styles.picture}><ImageWithBlur src={activityMeet} width={126} height={126}/></div>
              <div className={styles.title}>Meet other nomads from all around the world!</div>
              <div className={styles.content}>Co-work, join barbecue parties, visit movie nights and have fun</div>
            </div>
          </div>
        </div>
      </Screen>

      <CTAScreen titleRegular={'Barbaros '} titleBold={'Community'}
                 slogan={'Nomading is easier than you think'}
                 imageSrc={bckSomethingNew} imageAlt={'Surfing after work'}
                 description={<><p>Barbaros Hut stands for more than just coliving or coworking. We stand for a strong
                   community, work-life balance, and ease. Become a part of our community — follow us on social
                   media!</p>
                   <SocialIcons className={styles.social} analyticsPlacement={"hut"}/>
                 </>}
                 gray={true}
      />

      <FeedbackFormScreen ref={getInTouchForm} title={'Get in touch'} eventLabel={'hut'}
                          description={'Bookings, questions, tips, recommendations — anything goes! We’ll be in touch shortly.'}
                          withMessage={true} messageExample={'I’d like to book a room in Barbaros Hut!'}
      />

      <Screen id={styles.contacts}>
        <div className={styles.contact}>
          <div className={styles.firstContactRow}>
            <div className={styles.contactIcon}><Image src={"/images/icon-email.svg"} width={64} height={64} alt={""}/></div>
            <div className={styles.label}>email</div>
          </div>
          <a className={styles.value} href="mailto:welcome@satelstories.com">welcome@satelstories.com</a>
        </div>

        <div className={styles.contact}>
          <div className={styles.firstContactRow}>
            <div className={styles.contactIcon}><Image src={"/images/icon-address.svg"} width={64} height={64} alt={""}/></div>
            <div className={styles.label}>address</div>
          </div>
          <a className={styles.value} target={"_blank"} href={"https://goo.gl/maps/WLnaWtX7ZcK2ePNK6"}>Peniche,
            Portugal</a>
        </div>
      </Screen>

    </Layout>
  )
}
